/**
 * The source-file import graph: which files a component reaches, which
 * components reach a file, and which files no component's tree would show.
 *
 * Moved out of `designer/SourceFileTree.tsx` (EXT-1962) so the collector engine
 * can share one implementation with the tree: a Flux engine cannot import from
 * a React component that pulls in Spring and the assistant selectors.
 */
import {classifySourceFile} from './components';
import type {SourceFilesState} from '../flux/sourceFilesState';

// Extension candidates tried when resolving a relative import to a stored file.
const VITE_DEFAULT_EXTENSIONS = [
  '.mjs',
  '.js',
  '.mts',
  '.ts',
  '.jsx',
  '.tsx',
  '.json',
];
const VITE_INDEX_SUFFIXES = VITE_DEFAULT_EXTENSIONS.map(
  (extension) => `/index${extension}`
);

const VITE_JS_TO_TS_EXTENSIONS = [
  ['.mjs', ['.mts']],
  ['.cjs', ['.cts']],
  ['.js', ['.ts', '.tsx']],
  ['.jsx', ['.tsx']],
] as const;

// The sandbox tsconfig maps `@/*` to the sandbox root, so an aliased specifier
// resolves against the root rather than the importing file's directory.
const ALIAS_PREFIX = '@/';

// Bare package specifiers (`recharts`, `@webflow/react`) resolve to node_modules,
// never to a stored file. Note `@webflow/` fails this: the test is the literal
// two-character `@/`, not any `@` prefix.
const isResolvableSpecifier = (specifier: string): boolean =>
  specifier.startsWith('.') || specifier.startsWith(ALIAS_PREFIX);

const SPECIFIER_PATTERNS = [
  // Static `from '…'` / `import '…'`, which also covers `export … from`.
  /(?:from|import)(?:\s|\/\*[\s\S]*?\*\/|\/\/[^\n]*(?:\n|$))*['"]([^'"\n]+)['"]/g,
  // Dynamic `import('…')` — the static pattern cannot reach across the paren.
  /import(?:\s|\/\*[\s\S]*?\*\/|\/\/[^\n]*(?:\n|$))*\(\s*['"]([^'"\n]+)['"]/g,
  // Quoted and unquoted CSS `url(…)`, including `@import url(…)`.
  /url(?:\s|\/\*[\s\S]*?\*\/)*\((?:\s|\/\*[\s\S]*?\*\/)*['"]([^'"\n]+)['"](?:\s|\/\*[\s\S]*?\*\/)*\)/gi,
  /url(?:\s|\/\*[\s\S]*?\*\/)*\((?:\s|\/\*[\s\S]*?\*\/)*([^'"\s)]+)(?:\s|\/\*[\s\S]*?\*\/)*\)/gi,
];

/**
 * Import/export specifiers that can name a stored source file: relative
 * (`./types`, `../utils/format`) or `@/`-aliased.
 *
 * Dynamic `import()` counts. Missing it hid a lazy-loaded file from the tree of
 * the very component that loads it, and left the unused-file collector reading
 * `React.lazy(() => import('./Chart'))` as nothing importing `Chart` (EXT-1962).
 */
export function parseImportSpecifiers(code: string): string[] {
  const specifiers: string[] = [];
  for (const pattern of SPECIFIER_PATTERNS) {
    // `matchAll` does not advance the source regex's `lastIndex`, so these can
    // live at module level despite the `g` flag.
    for (const match of code.matchAll(pattern)) {
      const specifier = match[1];
      if (specifier !== undefined && isResolvableSpecifier(specifier)) {
        specifiers.push(specifier);
      }
    }
  }
  return specifiers;
}

/**
 * Per-content memo for one walk. A module shared by N components would
 * otherwise be re-parsed N times, making a pass cost components x files x
 * length instead of files x length.
 */
type ParseCache = Map<string, readonly string[]>;

const parseCached = (cache: ParseCache, code: string): readonly string[] => {
  const hit = cache.get(code);
  if (hit !== undefined) return hit;
  const parsed = parseImportSpecifiers(code);
  cache.set(code, parsed);
  return parsed;
};

// Resolves a relative specifier against a base directory to a normalized name
// (no leading slash, no extension), matching the `name` field of a source file.
function resolveSpecifierToName(baseDir: string, specifier: string): string {
  const segments = [
    ...(baseDir ? baseDir.split('/') : []),
    ...specifier.split('/'),
  ];
  const stack: string[] = [];
  for (const segment of segments) {
    if (segment === '' || segment === '.') continue;
    if (segment === '..') stack.pop();
    else stack.push(segment);
  }
  return stack.join('/');
}

/**
 * `name` -> store key, built once per walk.
 *
 * Resolution used to scan every file for each import specifier, so the
 * site-wide importer index cost roughly components x imports x files. Indexing
 * first makes each lookup constant.
 */
function buildNameIndex(files: SourceFilesState): Map<string, string> {
  const byName = new Map<string, string>();
  for (const [key, file] of Object.entries(files)) {
    if (!byName.has(file.name)) byName.set(file.name, key);
  }
  return byName;
}

function findKeyForResolvedName(
  byName: ReadonlyMap<string, string>,
  resolvedName: string
): string | undefined {
  const exactKey = byName.get(resolvedName);
  if (exactKey !== undefined) return exactKey;

  for (const [jsExtension, tsExtensions] of VITE_JS_TO_TS_EXTENSIONS) {
    if (!resolvedName.endsWith(jsExtension)) continue;
    const stem = resolvedName.slice(0, -jsExtension.length);
    for (const tsExtension of tsExtensions) {
      const key = byName.get(stem + tsExtension);
      if (key !== undefined) return key;
    }
    break;
  }

  for (const extension of VITE_DEFAULT_EXTENSIONS) {
    const key = byName.get(resolvedName + extension);
    if (key !== undefined) return key;
  }

  const packageJsonKey = byName.get(`${resolvedName}/package.json`);
  if (packageJsonKey !== undefined) return packageJsonKey;

  for (const suffix of VITE_INDEX_SUFFIXES) {
    const key = byName.get(resolvedName + suffix);
    if (key !== undefined) return key;
  }
  return undefined;
}

const stripImportPostfix = (specifier: string): string => {
  const postfixIndex = specifier.search(/[?#]/);
  return postfixIndex < 0 ? specifier : specifier.slice(0, postfixIndex);
};

/**
 * Walks relative imports outward from `mainCode`, returning every file key it
 * can reach. Shared by the per-component tree scope and the site-wide importer
 * index.
 */
function collectReachableKeys(
  mainCode: string,
  files: SourceFilesState,
  alwaysInclude: ReadonlySet<string> = new Set(),
  byName: ReadonlyMap<string, string> = buildNameIndex(files),
  parseCache: ParseCache = new Map()
): Set<string> {
  const includedKeys = new Set<string>();
  // Cursor rather than `shift()`, which is O(n) per dequeue.
  let cursor = 0;
  const queue: Array<{code: string; dir: string}> = [{code: mainCode, dir: ''}];
  // Seeded files are enqueued too, not just marked included: `includedKeys`
  // doubles as the visited set, so seeding it alone would stop the walk from
  // ever following what those files themselves import.
  for (const key of alwaysInclude) {
    const file = files[key];
    if (file === undefined) continue;
    includedKeys.add(key);
    const slashIndex = file.name.lastIndexOf('/');
    queue.push({
      code: file.content,
      dir: slashIndex >= 0 ? file.name.slice(0, slashIndex) : '',
    });
  }
  while (cursor < queue.length) {
    const current = queue[cursor++];
    if (current === undefined) break;
    for (const specifier of parseCached(parseCache, current.code)) {
      const pathSpecifier = stripImportPostfix(specifier);
      // `''` is the sandbox root, which is the base store `name` values are
      // relative to -- so `@/utils/format` resolves to `utils/format`.
      const resolvedName = pathSpecifier.startsWith(ALIAS_PREFIX)
        ? resolveSpecifierToName('', pathSpecifier.slice(ALIAS_PREFIX.length))
        : resolveSpecifierToName(current.dir, pathSpecifier);
      const key = findKeyForResolvedName(byName, resolvedName);
      if (key === undefined || includedKeys.has(key)) continue;
      includedKeys.add(key);
      const file = files[key];
      if (file !== undefined) {
        const slashIndex = file.name.lastIndexOf('/');
        const dir = slashIndex >= 0 ? file.name.slice(0, slashIndex) : '';
        queue.push({code: file.content, dir});
      }
    }
  }

  return includedKeys;
}

/**
 * Narrows site-wide source files to the current component's: reachable from
 * the main source via relative imports, plus anything sharing a folder with
 * one of those. Exported for unit testing.
 *
 * The folder half exists because reachability hides files the component owns
 * but never imports — a `README.md`, the generated `styles.css.d.ts` — which
 * reads as data loss across a reload (EXT-1969). A source file record has no
 * field naming its component, so the folder is the only ownership signal.
 *
 * `alwaysInclude` covers what neither reaches: a file just created at the root.
 *
 * A folder shared between components therefore shows in both trees, including
 * files this component does not import. That is accepted: shared code is the
 * point of multifile. Deletion still confirms, but without naming importers --
 * that warning is import-based, and a folder-widened file has none. Code
 * Functions are the one exception -- see `isFunctionFile`.
 *
 * A container grouping several components (`components/`, `src/`) widens too
 * far: every ancestor is registered, so importing `./components/pricing/Card`
 * pulls in every sibling component. Narrowing it by structure was tried and
 * reverted -- a container and a component folder owning a subfolder are the
 * same shape from names alone.
 */
export function scopeToComponent(
  mainCode: string,
  files: SourceFilesState,
  alwaysInclude: ReadonlySet<string> = new Set()
): SourceFilesState {
  const reachableKeys = collectReachableKeys(mainCode, files, alwaysInclude);
  const folders = collectComponentFolders(reachableKeys, files);
  const scoped: SourceFilesState = {};
  for (const [key, file] of Object.entries(files)) {
    if (
      reachableKeys.has(key) ||
      (!isFunctionFile(file.name) && isUnderAnyFolder(file.name, folders))
    ) {
      scoped[key] = file;
    }
  }
  return scoped;
}

/**
 * Function files sit outside the folder heuristic in both directions: they do
 * not register their folder, and no folder pulls them in.
 *
 * `functions/` is shared ground the way the sandbox root is -- every
 * component's handlers live there -- so one imported handler must not drag the
 * rest of the site's into this tree. Code Functions also define visibility the
 * other way round on purpose (EXT-1879): a handler enters a tree by being
 * imported, and the unused-function GC reads "nothing imports it" as "nothing
 * uses it" (EXT-1962). Widening would quietly break that.
 */
function isFunctionFile(name: string): boolean {
  return classifySourceFile(name) === 'function';
}

/** Directory part of a store `name`; empty for anything at the root. */
function dirOfName(name: string): string {
  const slashIndex = name.lastIndexOf('/');
  return slashIndex > 0 ? name.slice(0, slashIndex) : '';
}

/**
 * Folders holding at least one reachable file. The root is deliberately never
 * one: every component's main `.webflow.tsx` lives there, so widening to it
 * would pull the whole site into a single component's tree.
 */
function collectComponentFolders(
  reachableKeys: ReadonlySet<string>,
  files: SourceFilesState
): Set<string> {
  const folders = new Set<string>();
  for (const key of reachableKeys) {
    const file = files[key];
    if (file === undefined || isFunctionFile(file.name)) continue;
    // Every ancestor, not just the immediate parent: a component that keeps
    // its modules in a subfolder would otherwise register only that subfolder,
    // leaving a README beside the component itself hidden — the very symptom
    // being fixed.
    for (let dir = dirOfName(file.name); dir !== ''; dir = dirOfName(dir)) {
      folders.add(dir);
    }
  }
  return folders;
}

/**
 * Walks the file's own directories outward rather than prefix-matching its
 * name, so `pricing` cannot swallow `pricing-old` by construction instead of
 * by remembering a trailing slash.
 */
function isUnderAnyFolder(name: string, folders: ReadonlySet<string>): boolean {
  for (let dir = dirOfName(name); dir !== ''; dir = dirOfName(dir)) {
    if (folders.has(dir)) return true;
  }
  return false;
}

/**
 * A code component's identity and main source.
 *
 * Carries `id` as well as `name` because display names are not unique: two
 * components can share one, and the tree has to be able to tell the open
 * component apart from a namesake when deciding which importers are "other".
 */
export type ComponentSource = {id: string; name: string; code: string};

/**
 * Whether two source lists would produce the same walk.
 *
 * `selectSiteCodeComponentSources` rebuilds its array on every staged canvas
 * edit, so callers that key a walk on the array reference alone re-parse every
 * component's imports site-wide for edits that cannot affect the result. Only
 * `id` and `code` are read by the walk, so those are all this compares.
 */
export const componentSourcesEqual = (
  a: readonly ComponentSource[],
  b: readonly ComponentSource[]
): boolean =>
  a.length === b.length &&
  a.every((source, index) => {
    const next = b[index];
    return (
      next !== undefined && source.id === next.id && source.code === next.code
    );
  });

/** A component reaching a file, as shown in the shared-file affordances. */
export type FileImporter = {id: string; name: string};

/**
 * Maps each file key to the components that reach it through relative imports.
 *
 * Source files are site-scoped -- `SourceFile` carries a `siteId` and no
 * component reference -- so a module imported by two components is shared
 * state, and editing, renaming, or deleting it affects every component listed
 * here. The tree shows only the current component's files, which would
 * otherwise make that invisible right up until a destructive action breaks
 * something.
 */
export function buildFileImporterIndex(
  componentSources: readonly ComponentSource[],
  files: SourceFilesState
): ReadonlyMap<string, readonly FileImporter[]> {
  const importers = new Map<string, FileImporter[]>();
  // One index and one parse cache for every component's walk rather than one
  // per specifier and one per component.
  const byName = buildNameIndex(files);
  const parseCache: ParseCache = new Map();
  for (const {id, name, code} of componentSources) {
    for (const key of collectReachableKeys(
      code,
      files,
      undefined,
      byName,
      parseCache
    )) {
      const existing = importers.get(key);
      if (existing) existing.push({id, name});
      else importers.set(key, [{id, name}]);
    }
  }
  return importers;
}

/**
 * Keys no component's tree would show -- the site-wide dual of
 * {@link scopeToComponent}.
 *
 * The two `continue` guards are the negation of that function's `||`, in the
 * same order over the same helpers, so the two cannot disagree about what
 * "visible" means -- load-bearing, because the collector deletes what this
 * returns (EXT-1962) and an invisible file is unreachable through the UI.
 *
 * Folders are unioned in one call rather than per component because
 * `collectComponentFolders` has no cross-key state, so `folders(A) union
 * folders(B)` equals `folders(A union B)`.
 */
export function collectInvisibleKeys(
  componentSources: readonly ComponentSource[],
  files: SourceFilesState,
  alwaysInclude: ReadonlySet<string> = new Set()
): Set<string> {
  const byName = buildNameIndex(files);
  const parseCache: ParseCache = new Map();
  const reachable = new Set<string>();
  for (const {code} of componentSources) {
    for (const key of collectReachableKeys(
      code,
      files,
      alwaysInclude,
      byName,
      parseCache
    )) {
      reachable.add(key);
    }
  }
  // Only needed when there are no components: each component walk already
  // receives `alwaysInclude`, so this covers the site whose components were all
  // deleted while a file was held open.
  if (componentSources.length === 0) {
    for (const key of collectReachableKeys(
      '',
      files,
      alwaysInclude,
      byName,
      parseCache
    )) {
      reachable.add(key);
    }
  }
  const folders = collectComponentFolders(reachable, files);
  // Name resolution is first-key-wins, so when two keys share a name the loser
  // is unreachable by construction, not by anything the code says. Neither is
  // reported: a duplicate is the EXT-2106 resurrection shape, and which key
  // loses depends on iteration order.
  const keysByName = new Map<string, number>();
  for (const file of Object.values(files)) {
    keysByName.set(file.name, (keysByName.get(file.name) ?? 0) + 1);
  }
  const invisible = new Set<string>();
  for (const [key, file] of Object.entries(files)) {
    if (reachable.has(key)) continue;
    if ((keysByName.get(file.name) ?? 0) > 1) continue;
    if (!isFunctionFile(file.name) && isUnderAnyFolder(file.name, folders)) {
      continue;
    }
    invisible.add(key);
  }
  return invisible;
}
